<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import DateRange from '../../DateRange.svelte';
  import Pagination from '../../Pagination.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import Table from '../../Table.svelte';
  import InviteCharityModal from './InviteCharityModal.svelte';
  import { EmployerCharityService, type EmployerCharity, type EmployerCharityListParams } from '../../../lib/api/employer';

  // State for charities
  let charities: any[] = [];
  let loading = false;
  let error: string | null = null;

  // Pagination state
  let currentPage = 1;
  let totalPages = 1;
  let itemsPerPage = 10;
  let totalItems = 0;

  // Filter options
  let searchPhrase = '';
  let startDate = '';
  let endDate = '';

  // Add state for modal visibility
  let showInviteModal = false;

  // Function to toggle the invite modal
  function toggleInviteModal() {
    showInviteModal = !showInviteModal;
  }

  

  // Update the interface for the API response to include paging
  interface CharityResponse {
    success: boolean;
    data: any[] | {
      data: any[];
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
    paging?: {
      current_page: number;
      last_page: number;
      per_page: number;
      current_items: number;
      from: number;
      to: number;
      total: number;
    };
    message?: string;
  }

  async function loadCharities() {
    loading = true;
    error = null;
    
    try {
      // Store the requested page number
      const requestedPage = currentPage;
      
      // Prepare API parameters
      const params: EmployerCharityListParams = {
        page: requestedPage,
        per_page: itemsPerPage,
        column: 'created_at', // Default sort column
        order: 'desc'
      };

      // Add search phrase if provided
      if (searchPhrase) {
        params.phrase = searchPhrase;
      }

      // Add date range filters if provided
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      // Call the API
      const response = await EmployerCharityService.getList(params) as CharityResponse;
      
      if (response.success) {
        // Extract data from response
        if (Array.isArray(response.data)) {
          charities = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          charities = response.data.data;
        } else {
          charities = [];
        }
      
        // Update pagination if paging info is available
        if (response.paging) {
          totalPages = response.paging.last_page || 1;
          totalItems = response.paging.total || charities.length;
        } else if (response.data && typeof response.data === 'object' && 'last_page' in response.data) {
          totalPages = response.data.last_page || 1;
          totalItems = response.data.total || charities.length;
        } else {
          totalPages = 1;
          totalItems = charities.length;
        }

      } else {
        error = response.message || 'Failed to load charities';
        charities = [];
      }
    } catch (err: any) {
      console.error('Error loading charities:', err);
      error = err.message || 'An unexpected error occurred';
      charities = [];
    } finally {
      loading = false;
    }
  }

  function handlePageChange(event: CustomEvent<{page: number}>) {
    currentPage = event.detail.page;
    loadCharities();
  }

  function handleSearch() {
    currentPage = 1; // Reset to first page when searching
    loadCharities();
  }

  function handleDateRangeChange(event: CustomEvent<{startDate: string, endDate: string}>) {
    startDate = event.detail.startDate;
    endDate = event.detail.endDate;
    currentPage = 1; // Reset to first page when filtering
    loadCharities();
  }

  function viewCharity(uuid: string) {
    push(`/employer/view-charity/${uuid}`);
  }

  function formatDollar(amount: number | undefined): string {
    if (amount === undefined) return '$0';
    return `$${amount}`;
  }

  function formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }

  // Handle charity invitation success
  function handleCharityInvited() {
    loadCharities(); // Reload the list after successful invitation
  }

  // Define table columns
  const columns = [
    {
      key: 'name',
      label: 'Charity Name',
      width: '25%',
      render: (value: any, row: any) => {
        return row?.name || row?.charity?.name || 'N/A';
      }
    },
    {
      key: 'website',
      label: 'Website',
      width: '25%',
      render: (value: any, row: any) => {
        return row?.website || 'N/A';
      }
    },
    {
      key: 'total_donations',
      label: 'Total Donations',
      width: '20%',
      render: (value: any, row: any) => {
        return `$${row?.total_donations?.toFixed(2) || '0'}`;
      }
    },
    {
      key: 'last_donation',
      label: 'Last Donation',
      width: '20%',
      render: (value: any, row: any) => {
        return formatDate(row?.last_donation_date || row?.last_donation);
      }
    }
  ];

  function handleRowClick(row: any) {
    // Navigate to charity detail view
    const uuid = row?.charity?.uuid || row?.uuid;
    if (uuid) {
      viewCharity(uuid);
    }
  }

  onMount(() => {
    loadCharities();
  });
</script>

<!-- Add the invite modal component -->
<InviteCharityModal bind:showModal={showInviteModal} on:charityInvited={handleCharityInvited} />

<div class="charities-page">
  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-left">
      <SearchInput 
        placeholder="Search charities..." 
        bind:value={searchPhrase}
        on:search={handleSearch}
        width="250px"
      />
      
      <DateRange 
        bind:startDate={startDate}
        bind:endDate={endDate}
        placeholder="Date Range"
        on:apply={handleDateRangeChange}
      />
    </div>
    
    <div class="filters-right">
      <Button variant="primary" on:click={toggleInviteModal}>
        Invite Charity
      </Button>
    </div>
  </div>

  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading charities...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={loadCharities}>
        Try Again
      </Button>
    </div>
  {:else if charities.length === 0}
    <div class="empty-state">
      <p>No charities found</p>
      <Button variant="primary" on:click={() => { searchPhrase = ''; loadCharities(); }}>
        Reset Filters
      </Button>
    </div>
  {:else}
    <div class="table-section">
      <Table 
        columns={columns}
        data={charities}
        onRowClick={handleRowClick}
      />
    </div>

    {#if totalPages > 1}
      <div class="pagination-container">
        <Pagination 
          currentPage={currentPage} 
          totalPages={totalPages} 
          on:pageChange={handlePageChange} 
        />
      </div>
    {/if}
  {/if}
</div>

<style>
  .charities-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    background-color: #f7fafc;
    min-height: 100vh;
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    width: 100%;
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex: 1;
  }

  .filters-right {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  /* Table component styles */
  :global(.view-btn-charity-employer) {
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    background-color: #1f2937;
    color: white;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  :global(.view-btn-charity-employer:hover) {
    background-color: #374151;
    transform: translateY(-1px);
  }

  :global(.view-btn-charity-employer:active) {
    transform: translateY(0);
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid var(--color-primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: #dc2626;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .filters-right :global(button) {
    width: 170px;
    height: 52px;
    border-radius: 100px;
    padding: 10px;
    color: black;
    font-weight: 500;
    background-color: #f0b429;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .filters-section {
      flex-direction: column;
      gap: 1rem;
      align-items: stretch;
    }

    .filters-left {
      flex-direction: column;
      gap: 0.75rem;
    }

    .filters-right {
      width: 100%;
    }

    .filters-right :global(button) {
      width: 100%;
    }
  }
</style> 